import classNames from "classnames";
import Dropdown from "rc-dropdown";
import React, { useEffect, useMemo, useRef, useState } from "react";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "./ai-agent-node-dropdown.scss";

export interface IAgentNodeDropdownItem {
  key: string;
  label: string;
  value: string;
  group?: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

type AgentNodeDropdownKind = "model" | "memory" | "tools" | "actions";

export interface IAgentNodeDropdownProps {
  children: React.ReactNode;
  kind: AgentNodeDropdownKind;
  options: IAgentNodeDropdownItem[];
  placement?: string;
  getPopupContainer?: () => HTMLElement;
  closeOnSelect?: boolean;
}

interface IDropdownGroup {
  key: string;
  label: string;
  items: IAgentNodeDropdownItem[];
}

const DROPDOWN_CONFIG: Record<
  AgentNodeDropdownKind,
  {
    title: string;
    iconName: string;
    iconType: "common" | "datasource-icons" | "sidebar-icons";
  }
> = {
  tools: { title: "Tools", iconName: "wrench", iconType: "common" },
  model: {
    title: "LLM Model",
    iconName: "message-chatbot",
    iconType: "sidebar-icons"
  },
  memory: { title: "Memory", iconName: "memory", iconType: "datasource-icons" },
  actions: { title: "Actions", iconName: "ai-action", iconType: "datasource-icons" }
};

const AgentNodeDropdown: React.FC<IAgentNodeDropdownProps> = ({
  children,
  kind,
  options,
  placement = "bottomRight",
  getPopupContainer,
  closeOnSelect = true
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [closedGroupKeys, setClosedGroupKeys] = useState<Set<string>>(new Set());
  const instanceId = useRef(`agent-dropdown-${Math.random().toString(36).slice(2)}`).current;

  const config = DROPDOWN_CONFIG[kind];

  useEffect(() => {
    const handleCloseAll = () => {
      setOpen(false);
      setSearchTerm("");
    };
    const handleOtherOpened = (e: Event) => {
      const { id } = (e as CustomEvent<{ id: string }>).detail;
      if (id !== instanceId) {
        setOpen(false);
        setSearchTerm("");
      }
    };
    window.addEventListener("agent-dropdown-close-all", handleCloseAll);
    window.addEventListener("agent-dropdown-opened", handleOtherOpened);
    return () => {
      window.removeEventListener("agent-dropdown-close-all", handleCloseAll);
      window.removeEventListener("agent-dropdown-opened", handleOtherOpened);
    };
  }, [instanceId]);

  const groups: IDropdownGroup[] = useMemo(() => {
    const groupMap = new Map<string, IAgentNodeDropdownItem[]>();
    const ungrouped: IAgentNodeDropdownItem[] = [];

    options.forEach((option) => {
      if (option.group) {
        if (!groupMap.has(option.group)) {
          groupMap.set(option.group, []);
        }
        groupMap.get(option.group)!.push(option);
      } else {
        ungrouped.push(option);
      }
    });

    const result: IDropdownGroup[] = [];
    groupMap.forEach((items, label) => {
      result.push({ key: label, label, items });
    });
    if (ungrouped.length > 0) {
      result.push({ key: "__ungrouped__", label: "", items: ungrouped });
    }

    return result;
  }, [options]);

  const hasGroups = groups.length > 0 && !(groups.length === 1 && groups[0].key === "__ungrouped__");

  const isSearching = Boolean(searchTerm.trim());

  const filteredGroups: IDropdownGroup[] = useMemo(() => {
    if (!isSearching) {
      return groups;
    }
    const lower = searchTerm.toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.label.toLowerCase().includes(lower))
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, searchTerm, isSearching]);

  const handleItemClick = (item: IAgentNodeDropdownItem) => {
    item.onClick();
    if (closeOnSelect) {
      setOpen(false);
      setSearchTerm("");
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      window.dispatchEvent(new CustomEvent("agent-dropdown-opened", { detail: { id: instanceId } }));
    }
    setOpen(newOpen);
    if (!newOpen) {
      setSearchTerm("");
    } else {
      setTimeout(() => {
        document.querySelector<HTMLInputElement>(".agent-node-dropdown__search input")?.focus();
      }, 50);
    }
  };

  const handleGroupHeaderClick = (groupKey: string) => {
    setClosedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const overlay = (
    <div className="agent-node-dropdown__panel" onMouseDown={(e) => e.preventDefault()}>
      <div className="agent-node-dropdown__header">
        <KuikaSvg type={config.iconType} name={config.iconName} width={13} height={13} color="var(--dark-6)" />
        <span className="agent-node-dropdown__header-title">{config.title}</span>
        <KuikaInput
          className="agent-node-dropdown__search"
          placeholder="Search..."
          value={searchTerm}
          size="sm"
          onChange={(_e, value) => setSearchTerm(value as string)}
          rightIcon={<KuikaSvg type="common" name="search" width={12} height={12} color="var(--dark-5)" />}
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          autoComplete="off"
        />
      </div>

      <div className="agent-node-dropdown__body">
        {filteredGroups.length === 0 && <div className="agent-node-dropdown__empty">No results</div>}

        {filteredGroups.map((group) => {
          const isGroupOpen = isSearching || !hasGroups || !closedGroupKeys.has(group.key);

          return (
            <div key={group.key} className="agent-node-dropdown__group">
              {hasGroups && group.key !== "__ungrouped__" && (
                <button
                  type="button"
                  className="agent-node-dropdown__group-header"
                  onClick={() => handleGroupHeaderClick(group.key)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <KuikaSvg
                    type="arrows"
                    name="chevron-down"
                    width={12}
                    height={12}
                    color="var(--blue-5)"
                    className={classNames("agent-node-dropdown__chevron", {
                      "agent-node-dropdown__chevron--open": isGroupOpen
                    })}
                  />
                  <span className="agent-node-dropdown__group-label">{group.label}</span>
                </button>
              )}

              {isGroupOpen && (
                <div className="agent-node-dropdown__group-items">
                  {group.items.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={classNames("agent-node-dropdown__item", {
                        "agent-node-dropdown__item--no-group": !hasGroups || group.key === "__ungrouped__"
                      })}
                      onClick={() => handleItemClick(item)}
                      onMouseDown={(e) => e.preventDefault()}
                      style={{ paddingLeft: item.icon ? "24px" : "26px" }}
                    >
                      {item.icon && <span className="agent-node-dropdown__item-icon">{item.icon}</span>}
                      <span className="agent-node-dropdown__item-label">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <Dropdown
      overlayClassName="agent-node-dropdown"
      trigger={["click"]}
      overlay={overlay}
      visible={open}
      onVisibleChange={handleOpenChange}
      placement={placement as any}
      getPopupContainer={getPopupContainer}
    >
      <div>{children}</div>
    </Dropdown>
  );
};

export default AgentNodeDropdown;
